import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
  FaExclamationTriangle,
  FaWarehouse,
  FaBoxes,
} from "react-icons/fa";

import {
  PackageSearch,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const ReorderSparesReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "HO";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId || "8"
  );

  const [
    itemCode,
    setItemCode,
  ] = useState("");

  const [
    itemName,
    setItemName,
  ] = useState("");

  const [
    stockFilter,
    setStockFilter,
  ] = useState("");

  const [
    store,
    setStore,
  ] = useState("");

  const [
    category,
    setCategory,
  ] = useState("");

  /* =========================================================
     DROPDOWN DATA
  ========================================================= */

  const [
    categoryList,
    setCategoryList,
  ] = useState([]);

  const [
    storeList,
    setStoreList,
  ] = useState([]);

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  /* =========================================================
     RESULT DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     DESIGN CLASSES
     SAME DESIGN STYLE AS FIRST PAGE
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD DROPDOWNS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    const reorderDropdownReq =
      axios.get(
        "https://nissi-india.com/api/reOrder_sparesRpt",
        {
          params: {
            branchId:
              branchId ||
              storedBranchId ||
              "8",
          },

          headers:
            commonHeaders,
        }
      );

    const branchReq =
      axios.get(
        "https://nissi-india.com/api/CatWise_Rpt",
        {
          params: {
            branchType:
              storedBranchType ||
              "HO",

            branchId:
              branchId ||
              storedBranchId ||
              "8",
          },

          headers:
            commonHeaders,
        }
      );

    Promise.all([
      reorderDropdownReq,
      branchReq,
    ])
      .then(
        ([
          reorderRes,
          branchRes,
        ]) => {
          const stores =
            reorderRes.data.data
              ?.stores ||
            reorderRes.data
              .stores ||
            [];

          const categories =
            reorderRes.data.data
              ?.categories ||
            reorderRes.data
              .categories ||
            [];

          const branches =
            branchRes.data
              .branches ||
            [];

          setCategoryList(
            categories
          );

          setStoreList(
            stores
          );

          setBranchList(
            branches
          );

          if (
            !branchId &&
            branches.length > 0
          ) {
            setBranchId(
              branches[0]
                .value?.toString() ||
                storedBranchId ||
                "8"
            );
          }
        }
      )
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // preserve original one-time load behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     STOCK STATUS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const getStockStatus = (
    minStock,
    maxStock,
    closingStock
  ) => {
    if (
      closingStock <=
      minStock
    ) {
      return "critical";
    }

    if (
      closingStock <=
      maxStock * 0.3
    ) {
      return "low";
    }

    return "adequate";
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      branchId:
        branchId ||
        "%",

      storeId:
        store ||
        "%",

      categoryId:
        category ||
        "%",

      itemName:
        itemName ||
        "%",

      itemCode:
        itemCode ||
        "%",

      maxstock:
        stockFilter ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/reorderSpares_listRpt",
        {
          params:
            payload,

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data.data ||
          res.data.rows ||
          res.data.recordset ||
          res.data
            .recordsets?.[0] ||
          [];

        setData(rows);

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "Reorder spares fetch failed",
          err
        );

        toast.error(
          "Failed to fetch reorder spares report"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     EXPORT EXCEL
     EXISTING REAL FUNCTIONALITY PRESERVED
  ========================================================= */

  const handleExcelDownload = () => {
    const payload = {
      branchId:
        branchId ||
        "%",

      storeId:
        store ||
        "%",

      categoryId:
        category ||
        "%",

      itemName:
        itemName ||
        "%",

      itemCode:
        itemCode ||
        "%",

      maxstock:
        stockFilter ||
        "%",
    };

    axios
      .get(
        "https://nissi-india.com/api/reorderSpares_excel",
        {
          params:
            payload,

          headers:
            commonHeaders,

          responseType:
            "blob",
        }
      )
      .then((res) => {
        const url =
          window.URL.createObjectURL(
            new Blob([
              res.data,
            ])
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          url;

        link.setAttribute(
          "download",
          "ReorderSparesReport.xlsx"
        );

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        window.URL.revokeObjectURL(
          url
        );

        toast.success(
          "Excel file downloaded successfully"
        );
      })
      .catch((err) => {
        console.error(
          "Excel download failed",
          err
        );

        toast.error(
          "Failed to download Excel"
        );
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const payload = {
      branchId:
        branchId ||
        "%",

      storeId:
        store ||
        "%",

      categoryId:
        category ||
        "%",

      itemName:
        itemName ||
        "%",

      itemCode:
        itemCode ||
        "%",

      maxstock:
        stockFilter ||
        "%",
    };

    const query =
      new URLSearchParams(
        payload
      ).toString();

    window.open(
      `/inventory/ReorderSparesReport?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters = () => {
    setBranchId(
      storedBranchId ||
        "8"
    );

    setItemCode("");

    setItemName("");

    setStockFilter("");

    setStore("");

    setCategory("");

    setData([]);
  };

  /* =========================================================
     COUNTS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const criticalCount =
    useMemo(
      () =>
        data.filter(
          (item) =>
            getStockStatus(
              parseFloat(
                item.fld_minstock ||
                  0
              ),
              parseFloat(
                item.fld_maxstock ||
                  0
              ),
              parseFloat(
                item.fld_closing_stock ||
                  0
              )
            ) ===
            "critical"
        ).length,
      [data]
    );

  const lowCount =
    useMemo(
      () =>
        data.filter(
          (item) =>
            getStockStatus(
              parseFloat(
                item.fld_minstock ||
                  0
              ),
              parseFloat(
                item.fld_maxstock ||
                  0
              ),
              parseFloat(
                item.fld_closing_stock ||
                  0
              )
            ) ===
            "low"
        ).length,
      [data]
    );

  const adequateCount =
    data.length -
    criticalCount -
    lowCount;

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Reorder Spares Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <PackageSearch className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Reorder Spares Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Monitor and manage spare parts inventory levels
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT EXCEL */}

              <button
                type="button"
                onClick={
                  handleExcelDownload
                }
                disabled={
                  data.length ===
                  0
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <FaFileExcel
                  size={14}
                />

                Export Excel
              </button>

              {/* FILTER TOGGLE */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch Location
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.value ||
                            b.fld_Branchid
                          }
                          value={
                            b.value ||
                            b.fld_Branchid
                          }
                        >
                          {b.text ||
                            b.fld_BranchName}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* STORE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Store
                  </label>

                  <select
                    value={
                      store
                    }
                    onChange={(e) =>
                      setStore(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stores
                    </option>

                    {storeList.map(
                      (b) => (
                        <option
                          key={
                            b.value ||
                            b.fld_storeid
                          }
                          value={
                            b.value ||
                            b.fld_storeid
                          }
                        >
                          {b.text ||
                            b.fld_storename}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* CATEGORY */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Category
                  </label>

                  <select
                    value={
                      category
                    }
                    onChange={(e) =>
                      setCategory(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Categories
                    </option>

                    {categoryList.map(
                      (b) => (
                        <option
                          key={
                            b.value ||
                            b.fld_itemcategoryid
                          }
                          value={
                            b.value ||
                            b.fld_itemcategoryid
                          }
                        >
                          {b.text ||
                            b.fld_itemcategory_name}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* ITEM CODE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Code
                  </label>

                  <input
                    type="text"
                    value={
                      itemCode
                    }
                    onChange={(e) =>
                      setItemCode(
                        e.target.value
                      )
                    }
                    placeholder="Search by item code..."
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* ITEM NAME */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Name
                  </label>

                  <input
                    type="text"
                    value={
                      itemName
                    }
                    onChange={(e) =>
                      setItemName(
                        e.target.value
                      )
                    }
                    placeholder="Search by item name..."
                    className={
                      inputClass
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key ===
                        "Enter"
                      ) {
                        handleSearch();
                      }
                    }}
                  />
                </div>

                {/* STOCK LEVEL */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Stock Level
                  </label>

                  <select
                    value={
                      stockFilter
                    }
                    onChange={(e) =>
                      setStockFilter(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stock Levels
                    </option>

                    <option value="critical">
                      Critical Stock
                    </option>

                    <option value="low">
                      Low Stock
                    </option>

                    <option value="adequate">
                      Adequate Stock
                    </option>
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION ROW
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  disabled={
                    data.length ===
                    0
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            STOCK STATUS SUMMARY
            ORIGINAL COUNTS PRESERVED
        =================================================== */}

        {data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

            {/* CRITICAL */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-red-900/20 border-red-800/60"
                  : "bg-red-50 border-red-100"
              }`}
            >

              <div className="flex items-center gap-4">

                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    isDark
                      ? "bg-red-900/40"
                      : "bg-red-100"
                  }`}
                >
                  <FaExclamationTriangle className="text-red-500 text-lg" />
                </div>

                <div>

                  <div
                    className={`text-2xl font-bold ${
                      isDark
                        ? "text-red-300"
                        : "text-red-600"
                    }`}
                  >
                    {criticalCount}
                  </div>

                  <div
                    className={`text-xs font-medium mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Critical Stock
                  </div>
                </div>
              </div>
            </div>

            {/* LOW */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-yellow-900/20 border-yellow-800/60"
                  : "bg-yellow-50 border-yellow-100"
              }`}
            >

              <div className="flex items-center gap-4">

                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    isDark
                      ? "bg-yellow-900/40"
                      : "bg-yellow-100"
                  }`}
                >
                  <FaWarehouse className="text-yellow-500 text-lg" />
                </div>

                <div>

                  <div
                    className={`text-2xl font-bold ${
                      isDark
                        ? "text-yellow-300"
                        : "text-yellow-600"
                    }`}
                  >
                    {lowCount}
                  </div>

                  <div
                    className={`text-xs font-medium mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Low Stock
                  </div>
                </div>
              </div>
            </div>

            {/* ADEQUATE */}

            <div
              className={`rounded-2xl border p-5 shadow-sm ${
                isDark
                  ? "bg-green-900/20 border-green-800/60"
                  : "bg-green-50 border-green-100"
              }`}
            >

              <div className="flex items-center gap-4">

                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    isDark
                      ? "bg-green-900/40"
                      : "bg-green-100"
                  }`}
                >
                  <FaBoxes className="text-green-500 text-lg" />
                </div>

                <div>

                  <div
                    className={`text-2xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-600"
                    }`}
                  >
                    {adequateCount}
                  </div>

                  <div
                    className={`text-xs font-medium mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Adequate Stock
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Reorder Spares Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} items
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading reorder spares records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Part No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      UOM
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Location
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Min Stock
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Max Stock
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Current Stock
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => {
                      const stockStatus =
                        getStockStatus(
                          parseFloat(
                            item.fld_minstock ||
                              0
                          ),
                          parseFloat(
                            item.fld_maxstock ||
                              0
                          ),
                          parseFloat(
                            item.fld_closing_stock ||
                              0
                          )
                        );

                      return (
                        <tr
                          key={
                            index
                          }
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          {/* S.NO */}

                          <td className="px-4 py-3 font-medium">
                            {index + 1}
                          </td>

                          {/* ITEM CODE */}

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                stockStatus ===
                                "critical"
                                  ? isDark
                                    ? "bg-red-900/30 text-red-300"
                                    : "bg-red-100 text-red-700"
                                  : stockStatus ===
                                    "low"
                                  ? isDark
                                    ? "bg-yellow-900/30 text-yellow-300"
                                    : "bg-yellow-100 text-yellow-700"
                                  : isDark
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {item.fld_itemcode ||
                                "-"}
                            </span>
                          </td>

                          {/* ITEM NAME */}

                          <td className="px-4 py-3 font-medium">
                            {item.fld_itemname ||
                              "-"}
                          </td>

                          {/* PART NO */}

                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.fld_itempartno ||
                              "-"}
                          </td>

                          {/* UOM */}

                          <td className="px-4 py-3 whitespace-nowrap">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-blue-900/30 text-blue-300"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {item.fld_uomname ||
                                "-"}
                            </span>
                          </td>

                          {/* LOCATION */}

                          <td className="px-4 py-3">
                            {item.fld_location ||
                              "-"}
                          </td>

                          {/* MIN */}

                          <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                            {item.fld_minstock ??
                              0}
                          </td>

                          {/* MAX */}

                          <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                            {item.fld_maxstock ??
                              0}
                          </td>

                          {/* CURRENT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap text-right font-bold ${
                              stockStatus ===
                              "critical"
                                ? "text-red-500"
                                : stockStatus ===
                                  "low"
                                ? "text-yellow-500"
                                : "text-green-500"
                            }`}
                          >
                            {item.fld_closing_stock ??
                              0}
                          </td>

                          {/* STATUS */}

                          <td className="px-4 py-3 text-center">

                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                stockStatus ===
                                "critical"
                                  ? isDark
                                    ? "bg-red-900/30 text-red-300"
                                    : "bg-red-100 text-red-800"
                                  : stockStatus ===
                                    "low"
                                  ? isDark
                                    ? "bg-yellow-900/30 text-yellow-300"
                                    : "bg-yellow-100 text-yellow-800"
                                  : isDark
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {stockStatus ===
                                "critical" && (
                                <FaExclamationTriangle className="mr-1" />
                              )}

                              {stockStatus
                                .charAt(0)
                                .toUpperCase() +
                                stockStatus.slice(
                                  1
                                )}
                            </span>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBox
                size={42}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No inventory records
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {/* ===================================================
            SUMMARY CARD
            ORIGINAL VALUES PRESERVED
        =================================================== */}

        {data.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >

            {/* HEADER */}

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <div>

                  <h3 className="text-xl font-bold">
                    Summary
                  </h3>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Current spare stock level overview
                  </p>
                </div>
              </div>
            </div>

            {/* SUMMARY GRID */}

            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-4 gap-5">

                {/* TOTAL */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Total Items
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold text-${baseColor}-${baseShade}`}
                  >
                    {data.length}
                  </p>
                </div>

                {/* CRITICAL */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Critical Stock
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-red-300"
                        : "text-red-600"
                    }`}
                  >
                    {criticalCount}
                  </p>
                </div>

                {/* LOW */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Low Stock
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-yellow-300"
                        : "text-yellow-600"
                    }`}
                  >
                    {lowCount}
                  </p>
                </div>

                {/* ADEQUATE */}

                <div
                  className={`rounded-xl border p-5 ${
                    isDark
                      ? "bg-slate-800/60 border-slate-700"
                      : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-500"
                    }`}
                  >
                    Adequate Stock
                  </p>

                  <p
                    className={`mt-2 text-3xl font-bold ${
                      isDark
                        ? "text-green-300"
                        : "text-green-600"
                    }`}
                  >
                    {adequateCount}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default ReorderSparesReport;